import type {
  Configuration,
  Deployment,
  EnvironmentType,
  OperationAudit,
} from "../domain/models";
import { audits, configurations, deployments, services } from "../mocks/data";

export type DeliveryRoleId = "role-admin" | "role-ops-admin" | "role-operator" | "role-viewer";

export interface DeliveryActor {
  name: string;
  roleId: DeliveryRoleId;
  roleName: string;
}

export interface ConfigurationDiff {
  key: string;
  before?: string;
  after?: string;
  type: "added" | "changed" | "removed";
  sensitive: boolean;
}

export interface PublishConfigurationInput {
  serviceId: string;
  environment: EnvironmentType;
  namespace: string;
  baseVersion: number;
  content: Record<string, string>;
  sensitiveKeys: string[];
  changeNote: string;
  approvalNo: string;
  confirmProduction: boolean;
  idempotencyKey: string;
  actor: DeliveryActor;
}

export interface CreateReleaseInput {
  serviceId: string;
  environment: EnvironmentType;
  version: string;
  strategy: Deployment["strategy"];
  batchSize: number;
  intervalSeconds: number;
  canaryPercent?: number;
  changeNote: string;
  approvalNo: string;
  confirmProduction: boolean;
  idempotencyKey: string;
  actor: DeliveryActor;
}

export interface PublishConfigurationResult {
  configuration: Configuration;
  audit: OperationAudit;
  diff: ConfigurationDiff[];
}

export interface CreateReleaseResult {
  deployment: Deployment;
  audit: OperationAudit;
  previousVersion: string;
}

export interface DeliveryApi {
  listConfigurations(): Promise<Configuration[]>;
  listConfigurationHistory(serviceId: string, environment: EnvironmentType): Promise<Configuration[]>;
  listDeployments(): Promise<Deployment[]>;
  publishConfiguration(input: PublishConfigurationInput): Promise<PublishConfigurationResult>;
  createRelease(input: CreateReleaseInput): Promise<CreateReleaseResult>;
}

const wait = (ms = 320) => new Promise((resolve) => setTimeout(resolve, ms));
const allowedRoles = new Set<DeliveryRoleId>(["role-admin", "role-ops-admin"]);
const configurationRequests = new Map<string, PublishConfigurationResult>();
const releaseRequests = new Map<string, CreateReleaseResult>();

const formatDateTime = (date = new Date()) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const makeRequestId = () => `req-${Math.random().toString(16).slice(2, 8)}`;

const maskValue = (value: string | undefined, sensitive: boolean) =>
  sensitive && value !== undefined ? "******" : value;

const cloneConfiguration = (configuration: Configuration): Configuration => ({
  ...configuration,
  content: { ...configuration.content },
  sensitiveKeys: [...configuration.sensitiveKeys],
});

const configurationHistory = new Map<string, Configuration[]>();

for (const current of configurations) {
  const key = `${current.serviceId}:${current.environment}`;
  configurationHistory.set(key, [
    cloneConfiguration(current),
    {
      ...cloneConfiguration(current),
      id: `${current.id}-v${current.version - 1}`,
      version: current.version - 1,
      operator: "赵宁",
      updatedAt: "2026-07-12 14:22:18",
      changeNote: "同步生产基线",
    },
    {
      ...cloneConfiguration(current),
      id: `${current.id}-v${current.version - 2}`,
      version: current.version - 2,
      operator: "陈立",
      updatedAt: "2026-07-03 11:08:42",
      changeNote: "调整连接池参数",
    },
  ]);
}

const assertAuthorized = (actor: DeliveryActor) => {
  if (!allowedRoles.has(actor.roleId)) {
    throw new Error("当前角色无权执行生产配置或服务发布");
  }
};

const assertProductionConfirmation = (confirmed: boolean) => {
  if (!confirmed) throw new Error("请先完成生产环境风险确认");
};

const assertApprovalNo = (approvalNo: string) => {
  if (!/^CHG-\d{8}-\d{3,6}$/i.test(approvalNo.trim())) {
    throw new Error("变更单号格式应为 CHG-YYYYMMDD-序号");
  }
};

export function diffConfiguration(
  before: Record<string, string>,
  after: Record<string, string>,
  sensitiveKeys: string[],
): ConfigurationDiff[] {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return keys.reduce<ConfigurationDiff[]>((result, key) => {
    const sensitive = sensitiveKeys.includes(key);
    if (!(key in after)) {
      result.push({ key, before: maskValue(before[key], sensitive), type: "removed", sensitive });
      return result;
    }
    if (!(key in before)) {
      result.push({ key, after: maskValue(after[key], sensitive), type: "added", sensitive });
      return result;
    }
    if (before[key] !== after[key]) {
      result.push({
        key,
        before: maskValue(before[key], sensitive),
        after: maskValue(after[key], sensitive),
        type: "changed",
        sensitive,
      });
    }
    return result;
  }, []);
}

export const mockDeliveryApi: DeliveryApi = {
  async listConfigurations() {
    await wait(120);
    return [...configurationHistory.values()]
      .map((history) => cloneConfiguration(history[0]))
      .sort((left, right) => left.serviceId.localeCompare(right.serviceId));
  },

  async listConfigurationHistory(serviceId, environment) {
    await wait(100);
    return (configurationHistory.get(`${serviceId}:${environment}`) ?? []).map(cloneConfiguration);
  },

  async listDeployments() {
    await wait(120);
    return deployments.map((deployment) => ({ ...deployment }));
  },

  async publishConfiguration(input) {
    const duplicate = configurationRequests.get(input.idempotencyKey);
    if (duplicate) return duplicate;

    assertAuthorized(input.actor);
    assertProductionConfirmation(input.confirmProduction);
    assertApprovalNo(input.approvalNo);

    const historyKey = `${input.serviceId}:${input.environment}`;
    const history = configurationHistory.get(historyKey);
    const current = history?.[0];
    if (!current) throw new Error("目标服务配置不存在");
    if (current.version !== input.baseVersion) {
      throw new Error(`配置版本已更新，请基于最新 v${current.version} 重新提交`);
    }

    for (const sensitiveKey of current.sensitiveKeys) {
      if (input.content[sensitiveKey] !== current.content[sensitiveKey]) {
        throw new Error(`敏感配置 ${sensitiveKey} 不允许从前端修改`);
      }
    }

    const diff = diffConfiguration(current.content, input.content, current.sensitiveKeys);
    if (diff.length === 0) throw new Error("配置内容没有变化，无需发布");
    if (input.changeNote.trim().length < 4) throw new Error("请填写完整的变更说明");

    await wait(720);
    const now = formatDateTime();
    const next: Configuration = {
      ...cloneConfiguration(current),
      id: `CFG-${input.serviceId}-v${current.version + 1}`,
      namespace: input.namespace,
      version: current.version + 1,
      content: { ...input.content },
      sensitiveKeys: [...input.sensitiveKeys],
      operator: input.actor.name,
      updatedAt: now,
      changeNote: input.changeNote.trim(),
    };
    history.unshift(next);

    const latestIndex = configurations.findIndex((item) =>
      item.serviceId === input.serviceId && item.environment === input.environment);
    if (latestIndex >= 0) configurations.splice(latestIndex, 1, next);

    const audit: OperationAudit = {
      id: `AUD-${Date.now()}`,
      operator: input.actor.name,
      role: input.actor.roleName,
      action: "配置发布",
      resourceType: "Configuration",
      resourceName: `${input.serviceId}/${input.environment}`,
      parameters: `version=${next.version}, changedKeys=${diff.map((item) => item.key).join("|")}, approvalNo=${input.approvalNo.trim()}, mode=mock`,
      result: "success",
      environment: input.environment,
      ip: "127.0.0.1",
      createdAt: now,
      requestId: makeRequestId(),
    };
    audits.unshift(audit);
    const result = { configuration: cloneConfiguration(next), audit, diff };
    configurationRequests.set(input.idempotencyKey, result);
    return result;
  },

  async createRelease(input) {
    const duplicate = releaseRequests.get(input.idempotencyKey);
    if (duplicate) return duplicate;

    assertAuthorized(input.actor);
    assertProductionConfirmation(input.confirmProduction);
    assertApprovalNo(input.approvalNo);
    if (!/^v\d+\.\d+\.\d+(?:[-.][a-z0-9]+)*$/i.test(input.version.trim())) {
      throw new Error("目标版本格式应为 v主版本.次版本.修订版本");
    }
    if (input.batchSize < 1 || input.batchSize > 50) throw new Error("单批实例数应在 1 到 50 之间");
    if (input.intervalSeconds < 10 || input.intervalSeconds > 600) throw new Error("批次间隔应在 10 到 600 秒之间");
    if (input.strategy === "金丝雀发布" && (!input.canaryPercent || input.canaryPercent < 5 || input.canaryPercent > 50)) {
      throw new Error("金丝雀流量比例应在 5% 到 50% 之间");
    }
    if (input.changeNote.trim().length < 4) throw new Error("请填写完整的发布说明");

    const service = services.find((item) => item.id === input.serviceId);
    if (!service) throw new Error("目标服务不存在");
    if (service.version === input.version.trim()) throw new Error("目标版本与当前运行版本相同");

    await wait(920);
    const now = formatDateTime();
    const previousVersion = service.version;
    const deployment: Deployment = {
      id: `DEP-${String(Date.now()).slice(-6)}`,
      serviceId: service.id,
      version: input.version.trim(),
      environment: input.environment,
      operator: input.actor.name,
      createdAt: now,
      status: "success",
      strategy: input.strategy,
      changeNote: input.changeNote.trim(),
    };
    deployments.unshift(deployment);
    service.version = deployment.version;

    const audit: OperationAudit = {
      id: `AUD-${Date.now()}`,
      operator: input.actor.name,
      role: input.actor.roleName,
      action: "服务发布",
      resourceType: "Service",
      resourceName: service.name,
      parameters: `version=${deployment.version}, strategy=${input.strategy}, batchSize=${input.batchSize}, approvalNo=${input.approvalNo.trim()}, mode=mock`,
      result: "success",
      environment: input.environment,
      ip: "127.0.0.1",
      createdAt: now,
      requestId: makeRequestId(),
    };
    audits.unshift(audit);
    const result = { deployment: { ...deployment }, audit, previousVersion };
    releaseRequests.set(input.idempotencyKey, result);
    return result;
  },
};

// Production adapters remain disabled by default:
// ConfigPublishAdapter -> Nacos / Apollo / Spring Cloud Config approval pipeline
// ReleaseAdapter       -> Kubernetes / Argo Rollouts / deployment platform
export const deliveryApi: DeliveryApi = mockDeliveryApi;
